import { usePostHog } from "@posthog/react";
import { createRootRoute, Link, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect, useRef } from "react";
import { AppVersionPrompt } from "@/components/app-version-prompt";
import { Button } from "@/components/ui/button";
import { syncTrackedLocalStorageOwner } from "@/lib/local-storage-owner";
import { Toaster } from "@/components/ui/sonner";
import { useSession } from "@/hooks/use-session";
import { useBrandStore } from "@/state/store/brand";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";
import { useUserPreferencesStore } from "@/state/store/userPreferences";

function NotFoundComponent() {
      return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
                  <div className="text-center space-y-6">
                        <h1 className="text-8xl font-bold text-muted-foreground/30">404</h1>
                        <div className="space-y-2">
                              <h2 className="text-2xl font-semibold tracking-tight">Page not found</h2>
                              <p className="text-muted-foreground max-w-md">
                                    Sorry, we couldn't find the page you're looking for. It might have been moved or
                                    doesn't exist.
                              </p>
                        </div>
                        <Button asChild>
                              <Link to="/">Go back home</Link>
                        </Button>
                  </div>
            </div>
      );
}

function RouteErrorComponent({ error }: { error: unknown }) {
      const message = error instanceof Error ? error.message : "Something went wrong.";
      return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
                  <div className="text-center space-y-6 max-w-md">
                        <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
                        <p className="text-muted-foreground text-sm">{message}</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                              <Button asChild>
                                    <Link to="/">Go home</Link>
                              </Button>
                              <Button variant="outline" onClick={() => window.location.reload()}>
                                    Try again
                              </Button>
                        </div>
                  </div>
            </div>
      );
}

export const Route = createRootRoute({
      notFoundComponent: NotFoundComponent,
      errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
      component: RootComponent,
});

function RootComponent() {
      const posthog = usePostHog();
      const { sessionData } = useSession();
      const initializeSession = useBrandStore((state) => state.initializeSession);
      const initializeSearchSpecificationStore = useSearchSpecificationStore((state) => state.initializeUser);
      const initializeUserPreferencesStore = useUserPreferencesStore((state) => state.initializeUser);
      const lastIdentifyFingerprint = useRef<string | null>(null);
      const lastOrganizationGroup = useRef<string | null>(null);
      const isSessionBridgeRoute =
            typeof window !== "undefined" && window.location.pathname === "/session-bridge";

      useEffect(() => {
            if (isSessionBridgeRoute || !sessionData?.id) {
                  return;
            }

            syncTrackedLocalStorageOwner(sessionData.id);
            initializeSearchSpecificationStore(sessionData.id);
            initializeUserPreferencesStore(sessionData.id);

            if (sessionData.session_id) {
                  void initializeSession(sessionData.session_id);
            }

            const identifyFingerprint = [
                  sessionData.id,
                  sessionData.email,
                  sessionData.app_role,
                  sessionData.organization_id ?? "",
            ].join(":");

            if (lastIdentifyFingerprint.current !== identifyFingerprint) {
                  posthog.identify(sessionData.id, {
                        email: sessionData.email,
                        app_role: sessionData.app_role,
                        organization_id: sessionData.organization_id,
                  });
                  lastIdentifyFingerprint.current = identifyFingerprint;
            }

            if (sessionData.organization_id && lastOrganizationGroup.current !== sessionData.organization_id) {
                  posthog.group("organization", sessionData.organization_id);
                  lastOrganizationGroup.current = sessionData.organization_id;
            }
      }, [
            posthog,
            initializeSession,
            initializeSearchSpecificationStore,
            initializeUserPreferencesStore,
            sessionData?.id,
            sessionData?.email,
            sessionData?.app_role,
            sessionData?.organization_id,
            sessionData?.session_id,
            isSessionBridgeRoute,
      ]);

      return (
            <>
                  <Outlet />
                  <AppVersionPrompt enabled={!isSessionBridgeRoute} />
                  <Toaster />
                  <TanStackRouterDevtools position="bottom-right" />
            </>
      );
}
